import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { cn } from "@sylviq/ui-react-web/lib/cn";
import type { ReactNode } from "react";
import { ActionMenu, type ActionMenuItem } from "./ActionMenu.js";

export interface TableToolsProps {
  /** Accessible name for the toolbar. */
  label?: string;
  /** Search control, usually a labelled input. */
  search?: ReactNode;
  /** Filter controls for the current list. */
  filters?: ReactNode;
  /** App-owned table tools that are not filters. */
  tools?: ReactNode;
  /** Slot for extension-contributed table tools. */
  extensionTools?: ReactNode;
  /** Extra actions rendered before refresh and bulk actions. */
  actions?: ReactNode;
  onRefresh?: () => void;
  refreshLabel?: string;
  refreshing?: boolean;
  refreshingLabel?: string;
  /** Number of selected rows. The count is hidden at zero. */
  selectedCount?: number;
  /** Word after the count, e.g. "selected". */
  selectedLabel?: string;
  bulkActions?: readonly ActionMenuItem[];
  bulkActionsLabel?: string;
  bulkActionsAriaLabel?: string;
  /** Advanced filters or other controls shown on a second row. */
  children?: ReactNode;
  className?: string;
}

/** Shared toolbar for list views. Layout and spacing live here; the app
 * supplies the search control, filters, actions, and bulk-action items. */
export function TableTools({
  label = "Table tools",
  search,
  filters,
  tools,
  extensionTools,
  actions,
  onRefresh,
  refreshLabel = "Refresh",
  refreshing = false,
  refreshingLabel = "Refreshing…",
  selectedCount = 0,
  selectedLabel = "selected",
  bulkActions,
  bulkActionsLabel = "Bulk Actions",
  bulkActionsAriaLabel,
  children,
  className,
}: TableToolsProps) {
  return (
    <div
      role="toolbar"
      aria-label={label}
      className={cn("surface grid gap-3 rounded-2xl p-4", className)}
      data-testid="table-tools"
    >
      <div className="flex flex-wrap items-end gap-3">
        {search ? <div className="min-w-40 flex-1">{search}</div> : null}
        {filters ? (
          <div className="flex flex-wrap items-end gap-3">{filters}</div>
        ) : null}
        {tools ? (
          <div className="flex flex-wrap items-end gap-3">{tools}</div>
        ) : null}
        {extensionTools ? (
          <div
            className="flex flex-wrap items-end gap-3"
            data-slot="extension-tools"
          >
            {extensionTools}
          </div>
        ) : null}
        {actions}
        {onRefresh ? (
          <Button
            type="button"
            variant="secondary"
            onClick={onRefresh}
            disabled={refreshing}
          >
            {refreshing ? refreshingLabel : refreshLabel}
          </Button>
        ) : null}
        {selectedCount > 0 ? (
          <span
            aria-live="polite"
            className="text-xs text-[var(--muted-foreground)]"
          >
            {selectedCount} {selectedLabel}
          </span>
        ) : null}
        {bulkActions && bulkActions.length > 0 ? (
          <ActionMenu
            label={bulkActionsLabel}
            ariaLabel={bulkActionsAriaLabel ?? bulkActionsLabel}
            items={bulkActions}
          />
        ) : null}
      </div>
      {children ? <div className="grid gap-3">{children}</div> : null}
    </div>
  );
}
